/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';
import { RouteComponent, Router, createBrowserHistory } from 'react-resource-router';
import { AppProvider } from '@atlaskit/app-provider/app-provider';
import { RelayEnvironmentProvider } from '@atlassian/relay-environment-provider';
import { Root, Main } from '@atlaskit/navigation-system';
import { FlagsProvider } from '@atlaskit/flag';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { routes } from './routes';
import { useReactUFORouteWatcher } from '../analytics/react-ufo/useReactUFORouteWatcher';

import { initAnalyticsClient, type LoggedInUser } from '@atlassian/twg-microsite-experience';
import { LocaleProvider } from '@atlassian/twg-microsite-experience/locale-provider';
import { NavigationBar } from '@atlassian/twg-microsite-experience/NavigationBar';
import { PageErrorView } from '@atlassian/twg-microsite-experience/PageErrorView';
import { CLI_ROUTE_PATH, HOME_ROUTE_PATH } from '@atlassian/twg-microsite-experience/routes';
import { SentryErrorBoundary } from '@atlassian/twg-microsite-experience/sentry/error-boundary';
import { useResetScrollOnRouteChange } from '@atlassian/twg-microsite-experience/use-reset-scroll-on-route-change';
import { UserProvider } from '@atlassian/twg-microsite-experience/user-provider';
import { AnalyticsProvider } from '@atlassian/twg-microsite-experience/analytics-provider';
import { PanelProvider } from '@atlassian/twg-microsite-experience/panel-provider';
import { useCallback, useMemo } from 'react';
import { initializeFeatureGates } from '../feature-flags';
import { DebugPanel } from '@atlassian/twg-microsite-experience/DebugPanel';

// The microsite only registers a small, fixed set of routes, so any other
// pathname (e.g. `/hello`) is rewritten to `/` before the Router subscribes —
// this avoids an extra render pass or loading-state flicker on first mount for
// unknown paths. Known routes (including feature-gated ones like `/cli`) are
// left untouched so they can reach the Router; the route component itself is
// responsible for any gate-based fallback (e.g. redirecting `/cli` to `/` when
// the gate is off).
const KNOWN_PATHS = new Set<string>([HOME_ROUTE_PATH, CLI_ROUTE_PATH]);
const history = createBrowserHistory();
if (!KNOWN_PATHS.has(history.location.pathname)) {
	history.replace({ pathname: '/', search: history.location.search, hash: history.location.hash });
}

// Sibling of `<Root>` so it can subscribe to the `Router` context. The hook
// is a no-op on first render (the initial page-load is traced by `initUFO`)
// and fires `traceUFOTransition` on subsequent route name changes.
const RouteWatcher = (): null => {
	useReactUFORouteWatcher();
	return null;
};

// Also a sibling of `<Root>` so it can subscribe to the `Router` context.
// Returns the viewport to the top when routing between `/` and `/cli`, except
// for deep links, whose section scroll the destination page owns.
const ScrollReset = (): null => {
	useResetScrollOnRouteChange();
	return null;
};

export const App = (): JSX.Element => {
	const analyticsClient = useMemo(() => initAnalyticsClient(), []);

	// Re-identify the Statsig feature-gate client with the logged-in
	// user's identifiers. The anonymous Statsig bootstrap that runs in
	// `index.tsx` has already installed the `fg(...)` resolver, so this
	// call upgrades the existing client via `FeatureGates.updateUser`
	// rather than initializing from scratch — see `../feature-flags`.
	const onUserLoginComplete = useCallback(async (user: LoggedInUser) => {
		await initializeFeatureGates(user.accountId, user.orgId);
	}, []);

	return (
		<UFOSegment name="twg-microsite-root">
			<RelayEnvironmentProvider>
				<AnalyticsProvider analyticsClient={analyticsClient}>
					<AppProvider defaultColorMode="dark">
						<FlagsProvider>
							{/* Any component or provider that requires a FG to be initialized must be wrapped in UserProvider.
							    This also means there should be NO FGs being resolved outside of UserProvider */}
							<UserProvider onUserLoginComplete={onUserLoginComplete}>
								<LocaleProvider>
									<PanelProvider>
										<Router history={history} routes={routes}>
											<RouteWatcher />
											<ScrollReset />
											<Root>
												<Main>
													{/* The nav bar renders outside the route, so the page-level
													    boundaries inside `Home` and `CliPage` cannot catch a crash in
													    it — an uncaught one unmounts the whole app and leaves a bare
													    black page. Wrapping the nav bar and the route together turns
													    that into the same full-page error the pages show. */}
													<SentryErrorBoundary
														fallback={<PageErrorView />}
														loggerName="micrositeShell.errorBoundary"
													>
														<NavigationBar />
														<RouteComponent />
													</SentryErrorBoundary>
												</Main>
												<DebugPanel />
											</Root>
										</Router>
									</PanelProvider>
								</LocaleProvider>
							</UserProvider>
						</FlagsProvider>
					</AppProvider>
				</AnalyticsProvider>
			</RelayEnvironmentProvider>
		</UFOSegment>
	);
};
